import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BsPlusLg } from "react-icons/bs";
import { HiOutlineMinus } from "react-icons/hi2";
import useForm from "../../../hooks/useForm";
import { setFormDataGlobState } from "../../../store";
import {
  ACADEMIC_LEVELS,
  DEADLINES,
  SPACINGS,
  TYPE_OF_ENGLISH,
  TYPE_OF_PAPERS,
  WRITING_TYPES,
} from "../../../Data/orderProperties";
import { USER_INPUT_FIELD_TYPES } from "../../../constants/userInputFieldNames";
import ProgressBar from "../../../components/Form/ProgressBar";
import DropDown from "../../../components/Form/Dropdown";
import Checkbox from "../../../components/Form/Checkbox";
import CustomButton from "../../../components/Form/ArrowButton";
import { ClassFilled } from "@fluentui/react-icons";
import { calculateCost } from "../../../utils/costCalculator";
import showToast from "../../../utils/toastNotifications";
import { SPACING_IDENTIFIERS, TYPE_OF_ENGLISH_INDETIFIERS, WRITING_TYPES_IDENTIFIERS } from "../../../constants/commonConstants";
import { calculateLocalDeadline } from "../../../utils/formatting";

const Calculation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const createOrder = useSelector((state) => state.createOrder);

  // Thunks
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      writingType: WRITING_TYPES_IDENTIFIERS.WRITING_FROM_SCRATCH,
      typeOfPaper: "",
      academicLevel: "",
      typeOfEnglish: TYPE_OF_ENGLISH_INDETIFIERS.US_ENGLISH,
      deadline: "",
      spacing: SPACING_IDENTIFIERS.DOUBLE_SPACING,
      needPresentation: false,
      needDocumentation: false, // Only for "PowerPoint"
      budgetIncrement: 0
    },
    {
      writingType: true,
      typeOfPaper: true,
      typeOfEnglish: true,
      academicLevel: true,
      deadline: true,
      spacing: true,
      needPresentation: false,
      needDocumentation: false,
      budgetIncrement: true,
    },
    {
      writingType: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      typeOfPaper: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      academicLevel: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      typeOfEnglish: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      deadline: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      spacing: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
      needPresentation: USER_INPUT_FIELD_TYPES.BOOLEAN_FIELD,
      needDocumentation: USER_INPUT_FIELD_TYPES.BOOLEAN_FIELD,
      budgetIncrement: USER_INPUT_FIELD_TYPES.REQUIRED_FIELD,
    }
  );

  // States
  const [count, setCount] = useState(1);
  const [slidescount, setSlidesCount] = useState(1);
  const [totalPages, setTotalPages] = useState(0)

  // Progressbar functions
  const steps = [
    { fullLabel: "Calculation", shortLabel: "Calculation" },
    { fullLabel: "Paper Instructions", shortLabel: "Instructions" },
    { fullLabel: "Secure Payment", shortLabel: "Payment" },
  ];

  // Quantity Button Functions
  const increment = () => setCount((count) => totalPages < 100 ? count + 1 : count);
  const decrement = () => setCount(count > 1 ? count - 1 : 1);
  const incrementSlidesCount = () => {
    setSlidesCount((prev) => totalPages < 100 ? prev + 1 : prev);
  };
  const decrementSlidesCount = () => {
    setSlidesCount((prev) => (prev > 1 ? prev - 1 : 1));
  };

  // CheckBox Functions
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleBudgetDecrement = () => {
    if (formData.budgetIncrement > 0) {
      setFormData(prev => ({ ...prev, budgetIncrement: prev.budgetIncrement - 1 }))
    }
  }

  //Next Button Function
  const handleNextClick = () => {
    // Mark all fields as touched
    setTouched({
      writingType: true,
      typeOfPaper: true,
      academicLevel: true,
      typeOfEnglish: true,
      deadline: true,
      spacing: true,
      needPresentation: true,
      needDocumentation: true,
      budgetIncrement: true,
    });

    console.log("validate", validateForm());

    if (validateForm()) {
      let slides = 0;
      let pages = 0;

      if (formData.typeOfPaper === "Powerpoint") {
        if (formData.needPresentation) {
          slides = slidescount || 1;
        }
        if (formData.needDocumentation) {
          pages = count || 1;
        }
      } else {
        pages = formData.needDocumentation ? count || 1 : 0;
        slides = formData.needPresentation ? slidescount || 1 : 0;
      }

      dispatch(
        setFormDataGlobState({
          ...formData,
          slides,
          pages,
        })
      );

      navigate("/paper-instructions");
    }
  };

  useEffect(() => {
    if (
      createOrder &&
      createOrder.formData &&
      Object.keys(createOrder.formData)?.length > 0
    ) {
      setFormData({
        ...formData,
        writingType: createOrder.formData?.writingType,
        typeOfPaper: createOrder.formData?.typeOfPaper,
        academicLevel: createOrder.formData?.academicLevel,
        typeOfEnglish: createOrder.formData?.typeOfEnglish,
        deadline: createOrder.formData?.deadline,
        spacing: createOrder.formData?.spacing,
        needPresentation: createOrder.formData?.needPresentation,
        needDocumentation: createOrder.formData?.needDocumentation,
        budgetIncrement: createOrder.formData?.budgetIncrement,
      });
      setCount(createOrder.formData.pages);
      setSlidesCount(createOrder.formData.slides);
    }
  }, [createOrder]);

  //set total number of pages according to input parameters
  useEffect(() => {
    let totalPages;
    if (formData.typeOfPaper === "Powerpoint") {
      if (formData.needDocumentation) {
        totalPages = count + slidescount
      } else {
        totalPages = slidescount
      }
    } else {
      if (formData.needPresentation) {
        totalPages = count + slidescount
      } else {
        totalPages = count
      }
    }
    if (totalPages >= 100) {
      showToast("error", "Maximum limit of pages and slides count is reached");
    }
    setTotalPages(totalPages)
  },
    [
      count,
      slidescount,
      formData.needDocumentation,
      formData.needPresentation
    ])

  // console.log("createOrder", createOrder.formDataFiles);

  return (
    <div className="flex flex-col flex-grow h-screen min-w-[340px] overflow-x-hidden">
      <div className="text-primary font-roboto font-semibold text-[24px] pb-4 ml-[24px] xl:mt-[24px] mt-[104px]">
        <h2 className=" mr-2">Order Now</h2>
      </div>
      <div className="flex flex-col flex-grow overflow-y-auto">
        <div className="bg-white  mt-[32px] ml-6 xl:ml-[48px] mr-[24px] lg:mr-[48px] mb-[48px] border border-medium-gray rounded-lg py-8 px-[16px] lg:px-[48px] xl:px-[136px]   md:py-10  max-w-full md:max-w-[2200px] w-auto mx-auto">
          <ProgressBar steps={steps} activeStep={1} completedStep={0} />{" "}
          <div className="flex flex-col justify-center items-center ">
            {/* Grid Layout for Dropdowns and Pages Section */}
            <div
              className="grid
            grid-cols-1 
            sm:grid-cols-2 
            md:grid-cols-2 
            lg:grid-cols-2 
            xl:grid-cols-2 
            2xl:grid-cols-2 
            gap-x-6 mt-10 w-full px-0 
            "
            >
              {/* First Row: Two Dropdowns */}
              <div>
                <DropDown
                  label="Writing Type"
                  id="wrtingType"
                  name="writingType"
                  value={formData.writingType}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.writingType && errors.writingType}
                  defaultOption="Select the Writing Type"
                  idKey="id"
                  valueKey="name"
                  options={WRITING_TYPES}
                  dropdownDivStyle="w-full max-h-[200px] sm:w-full lg:w-full"
                // error={selectedValue === "" ? "Please select an option" : ""}
                />
              </div>
              <div>
                <DropDown
                  label="Type of Paper"
                  id="typeOfPaper"
                  name="typeOfPaper"
                  value={formData.typeOfPaper}
                  onChange={(name, value) => {
                    errors.typeOfPaper = "";
                    const isPowerpoint = value === "Powerpoint";
                    setFormData((prev) => ({
                      ...prev,
                      typeOfPaper: value,
                      needDocumentation: !isPowerpoint,
                      needPresentation: isPowerpoint,
                    }));
                  }}
                  onBlur={handleBlur}
                  error={touched.typeOfPaper && errors.typeOfPaper}
                  defaultOption="Select the type of Paper"
                  idKey="id"
                  valueKey="name"
                  options={TYPE_OF_PAPERS}
                  dropdownDivStyle="w-full max-h-[200px] sm:w-full lg:w-full"
                // error={selectedValue === "" ? "Please select an option" : ""}
                />
              </div>
              {/* Second Row: Two Dropdowns */}
              <div>
                <DropDown
                  label="Academic Level"
                  id="academicLevel"
                  name="academicLevel"
                  value={formData.academicLevel}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.academicLevel && errors.academicLevel}
                  defaultOption="Select the Academic Level"
                  idKey="id"
                  valueKey="name"
                  options={ACADEMIC_LEVELS}
                  dropdownDivStyle="w-full max-h-[200px] sm:w-full lg:w-full"
                  // error={selectedValue === "" ? "Please select an option" : ""}
                />
              </div>
              <div>
                <DropDown
                  label="Type of English"
                  id="typeOfEnglish"
                  name="typeOfEnglish"
                  value={formData.typeOfEnglish}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.typeOfEnglish && errors.typeOfEnglish}
                  defaultOption="Select the English Type"
                  idKey="id"
                  valueKey="name"
                  options={TYPE_OF_ENGLISH}
                  dropdownDivStyle="w-full max-h-[200px] sm:w-full lg:w-full"
                  extraAdderKey="extraAdder"
                // error={selectedValue === "" ? "Please select an option" : ""}
                />
              </div>
              {/* Second Row: Two Dropdowns */}
              <div>
                <DropDown
                  label="Deadline"
                  id="deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.deadline && errors.deadline}
                  defaultOption="Select the Deadline"
                  idKey="id"
                  valueKey="name"
                  options={DEADLINES}
                  dropdownDivStyle="w-full max-h-[200px] sm:w-full lg:w-full"
                // error={selectedValue === "" ? "Please select an option" : ""}
                />
                {formData.deadline && formData.deadline?.length > 0 &&
                  <div className="mt-1 pl-0 font-medium text-primary -translate-y-5">
                    To be delivered by {calculateLocalDeadline(formData.deadline) || ""}
                  </div>
                }
              </div>
              <div>
                <DropDown
                  label="Spacing"
                  id="spacing"
                  name="spacing"
                  value={formData.spacing}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.spacing && errors.spacing}
                  defaultOption="Select the Spacing"
                  idKey="id"
                  valueKey="name"
                  options={SPACINGS}
                  dropdownDivStyle="w-full max-h-[200px] sm:w-full lg:w-full"
                // error={selectedValue === "" ? "Please select an option" : ""}
                />
              </div>

              {/* Third Row: Pages and Slides Section */}
              <div className="sm:col-span-1">
                <div className="flex flex-col items-start">
                  {/* Render this only if typeOfPaper is "Powerpoint" */}
                  {formData.typeOfPaper === "Powerpoint" ? (
                    <>
                      {/* Slides Count Section */}
                      <div className="flex flex-col items-start mb-4">
                        <label className="font-medium text-sm sm:text-base text-secondary mb-2">
                          Slides <span className="text-light-red">*</span>
                        </label>
                        {/* Wrap the button group and text in a flex container */}
                        <div className="flex items-center space-x-[36px]">
                          <div className="flex items-center bg-soft-gray rounded-lg px-4 py-2 space-x-4 w-[127px] justify-between">
                            <button onClick={decrementSlidesCount}>
                              <HiOutlineMinus className="h-5 w-5 text-black" />
                            </button>
                            <span className="font-semibold text-base">
                              {slidescount}
                            </span>
                            <button
                              onClick={incrementSlidesCount}
                              className="h-5 w-5 text-black"
                            >
                              <BsPlusLg />
                            </button>
                          </div>
                          {/* Add text next to the button group */}
                          <span className="text-sm sm:text-base text-black font-medium">
                            {1 * slidescount} Slide
                          </span>
                        </div>
                      </div>

                      {/* Checkbox for Pages */}
                      <Checkbox
                        name="needDocumentation"
                        checked={formData.needDocumentation}
                        onChange={handleCheckboxChange}
                        value="agree"
                        label="I want documentation also."
                        containerStyle=""
                      />

                      {/* Pages Count Section */}
                      {formData.needDocumentation && (
                        <div className="flex flex-col items-start mt-6">
                          <label className="font-medium text-sm sm:text-base text-secondary mb-2">
                            Pages <span className="text-light-red">*</span>
                          </label>
                          {/* Wrap the button group and text in a flex container */}
                          <div className="flex items-center space-x-[36px]">
                            <div className="flex items-center bg-soft-gray rounded-lg px-4 py-2 space-x-4 w-[127px] justify-between">
                              <button onClick={decrement}>
                                <HiOutlineMinus className="h-5 w-5 text-black" />
                              </button>
                              <span className="font-semibold text-base">
                                {count}
                              </span>
                              <button
                                onClick={increment}
                                className="h-5 w-5 text-black"
                              >
                                <BsPlusLg />
                              </button>
                            </div>
                            {/* Add text next to the button group */}
                            <span className="text-sm sm:text-base font-inter text-black font-medium">
                              {275 * (formData.spacing === SPACING_IDENTIFIERS.SINGLE_SPACING ? 2 : 1) * count} words
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Pages Count Section */}
                      <div className="flex flex-col items-start mb-4 ">
                        <label className="font-medium text-sm sm:text-base text-secondary mb-2">
                          Pages <span className="text-light-red">*</span>
                        </label>
                        {/* Wrap the button group and text in a flex container */}
                        <div className="flex items-center space-x-[36px]">
                          <div className="flex items-center bg-soft-gray rounded-lg px-4 py-2 space-x-4 w-[127px] justify-between">
                            <button onClick={decrement}>
                              <HiOutlineMinus className="h-5 w-5 text-black" />
                            </button>
                            <span className="font-semibold text-base">
                              {count}
                            </span>
                            <button
                              onClick={increment}
                              className="h-5 w-5 text-black"
                            >
                              <BsPlusLg />
                            </button>
                          </div>
                          {/* Add text next to the button group */}
                          <span className="text-sm sm:text-base font-inter text-black font-medium">
                            {275 * (formData.spacing === SPACING_IDENTIFIERS.SINGLE_SPACING ? 2 : 1) * count} words
                          </span>
                        </div>
                      </div>

                      {/* Checkbox for Slides */}
                      <Checkbox
                        name="needPresentation"
                        checked={formData.needPresentation}
                        onChange={handleCheckboxChange}
                        value="agree"
                        label="I want presentation slides also."
                        containerStyle=""
                      />

                      {/* Slides Count Section */}
                      {formData.needPresentation && (
                        <div className="flex flex-col items-start mt-6">
                          <label className="font-medium text-sm sm:text-base text-secondary mb-2">
                            Slides <span className="text-light-red">*</span>
                          </label>
                          {/* Wrap the button group and text in a flex container */}
                          <div className="flex items-center space-x-[36px]">
                            <div className="flex items-center bg-soft-gray rounded-lg px-4 py-2 space-x-4 w-[127px] justify-between">
                              <button onClick={decrementSlidesCount}>
                                <HiOutlineMinus className="h-5 w-5 text-black" />
                              </button>
                              <span className="font-semibold text-base">
                                {slidescount}
                              </span>
                              <button
                                onClick={incrementSlidesCount}
                                className="h-5 w-5 text-black"
                              >
                                <BsPlusLg />
                              </button>
                            </div>
                            {/* Add text next to the button group */}
                            <span className="text-sm sm:text-base text-black font-medium">
                              {1 * slidescount} Slide
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Checkbox Section */}
            {/* <div className="flex flex-col justify-start w-full mt-6">
              <Checkbox
                checked={formData.removeAiContent}
                onChange={handleCheckboxChange}
                value="agree"
                label="Just remove AI content from my existing paper."
                containerStyle=""
                name="removeAiContent"
              />
              {errors["removeAiContent"] && (
                <p className="ml-11 text-red-400">
                  {errors["removeAiContent"]}
                </p>
              )}
            </div> */}

            {/* Total Cost */}


            <div className="flex justify-center my-6 text-center ">
              <div className="font-semibold text-lg sm:text-xl text-primary">
                <div className="flex flex-col font-semibold text-lg sm:text-xl text-primary">
                  <div>
                    Total Cost (USD):{" "}
                    <span className="text-[30px]">
                      {calculateCost({
                        academicLevel: formData.academicLevel,
                        deadline: formData.deadline,
                        spacing: formData.spacing,
                        pages: totalPages, //count + slidescount,
                        typeOfPaper: formData.typeOfPaper,
                        budgetIncrement: formData.budgetIncrement,
                        writingType: formData.writingType,
                        typeOfEnglish: formData.typeOfEnglish
                      })}
                    </span>
                  </div>
                  {formData.academicLevel && formData.deadline && formData.typeOfPaper &&
                    <>
                      <div className="flex w-full mt-1 ">
                        <span className="mr-aut0 text-[14px]">Increase My Budget by</span>
                      </div>
                      <div className="w-full flex w-full gap-3 justify-center">
                        <button
                          className="text-[26px]"
                          onClick={handleBudgetDecrement}>
                          -
                        </button>
                        <div className="px-2 min-w-[50px]">
                          <span className="text-[18px]">${formData.budgetIncrement}</span>
                        </div>
                        <button
                          className="text-[26px]"
                          onClick={() => setFormData(prev => ({ ...prev, budgetIncrement: prev.budgetIncrement + 1 }))}
                        >
                          +
                        </button>
                      </div>
                    </>
                  }
                </div>
              </div>
            </div>

            {/* Next Button */}
            <div className="flex justify-between mt-6 w-full">
              <CustomButton
                text="Back"
                onClick={() => { }}
                width="120px"
                height="44px"
                iconDirection="left"
                iconPosition="before"
                disabled={true}
              />

              <CustomButton
                text="Next"
                onClick={handleNextClick}
                width="120px"
                height="44px"
                iconDirection="right"
                iconPosition="after"
              />
            </div>

            <div className="w-full flex justify-center">
              <div className="bg-[#eef6fb] text-[#222] text-[13.5px] px-[10px] py-[6px] flex justify-center items-center flex-wrap border-b border-[#d0e3f0] gap-[20px] mt-[20px]">
                <div className="flex items-center gap-[6px] before:content-['🗸'] before:text-[#007acc] before:text-[14px]">
                  Free Turnitin AI Report (0% AI Guarantee)
                </div>
                <div className="flex items-center gap-[6px] before:content-['🗸'] before:text-[#007acc] before:text-[14px]">
                  Free Turnitin Similarity Report (No Plagiarism)
                </div>
                <div className="flex items-center gap-[6px] before:content-['🗸'] before:text-[#007acc] before:text-[14px]">
                  Quality Papers (No Reckless Grammar Errors)
                </div>
                <div className="flex items-center gap-[6px] before:content-['🗸'] before:text-[#007acc] before:text-[14px]">
                  On-Time Delivery (No Late Deliveries)
                </div>
              </div>
            </div> 

          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculation;
